import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { apiClient } from '../shared/apiClient';


export interface User {
  id: string;
  username: string;
  buildingId: string;
  name: string;
  email: string;
  role: 'admin' | 'manager' | 'tenant';
  tenant_group_ids: string[];
}
export interface Jwt {
  user: User;
  exp: number;
}

export interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (username: string, password: string) => Promise<void>;
  logout: () => void;
  loading: boolean;
  isAuthenticated: boolean;
  hasRole: (...roles: Array<'admin' | 'manager' | 'tenant'>) => boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const logoutUser = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  context.logout();
};

interface AuthProviderProps {
  children: ReactNode;
}

  // decode the logged in user
const parseJwt = (token: string ) : Jwt => {
  if (!token) {
    return {exp: 0, user: {id: '', username: '', buildingId: '', name: '', email: '', role: 'admin', tenant_group_ids: []}};
  }
  const base64Url = token.split(".")[1];
  const base64 = base64Url.replace("-", "+").replace("_", "/");
  console.log("JWT:");
  console.log(JSON.parse(window.atob(base64)));
  return JSON.parse(window.atob(base64)) as Jwt;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const isAuthenticated = !!user && !!token;

  const hasRole = (...roles: Array<'admin' | 'manager' | 'tenant'>): boolean => {
    return user ? roles.includes(user.role) : false;
  };

  const login = async (username: string, password: string): Promise<void> => {
    try {
      const data = await apiClient.login(username, password);
      const token = data.jwt;
      const token_decoded = parseJwt(token);
      if(token_decoded.exp == 0){
        throw new Error('Invalid token');
      }
      const user_data : User = token_decoded?.user;
      setUser(user_data);
      setToken(token);
      
      // Update apiClient with the new token
      apiClient.setToken(token);
      
      // Store token in localStorage for persistence
      localStorage.setItem('auth_token', token);
      localStorage.setItem('auth_user', JSON.stringify(user_data));
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  };

  const logout = () => {
    setUser(null);
    setToken(null);
    apiClient.setToken(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
  };

  const verifyToken = async (token: string) => {
    try {
      // Set token in apiClient for this verification request
      apiClient.setToken(token);
      const userData = await apiClient.getMe();
      const token_rx = userData.jwt;
      const token_decoded = parseJwt(token_rx);
      if(token_decoded.exp == 0){
        throw new Error('Invalid token');
      }
      const user_data : User = token_decoded?.user;
      setUser(user_data);
      setToken(token_rx);
      return true;
    } catch (error) {
      console.error('Token verification error:', error);
      // Token is invalid or user not found
      apiClient.setToken(null);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_user');
      return false;
    }
  };

  // Initialize auth state from localStorage on app start
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const storedToken = localStorage.getItem('auth_token');
        const storedUser = localStorage.getItem('auth_user');

        if (storedToken && storedUser) {
          // Verify token is still valid
          const isValid = await verifyToken(storedToken);
          if (!isValid) {
            // Token invalid, clear storage
            localStorage.removeItem('auth_token');
            localStorage.removeItem('auth_user');
          }
        }
      } catch (error) {
        console.error('Auth initialization error:', error);
        // Clear potentially corrupted data
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
      } finally {
        setLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    loading,
    isAuthenticated,
    hasRole,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Export the apiClient for use in other components
export { apiClient };